import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from '@angular/core';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';

import { EditorActions } from '../../../enums/enums';
import { ProductionCommandType } from '../../../utils/ml-2-html-utils';
import { StoryTextEditorBase } from '../st-story-text-editor.base';

@Component({
    selector: 'st-story-basic-text-editor',
    imports: [FroalaEditorModule],
    templateUrl: './st-story-basic-text-editor.html',
    styleUrl: './st-story-basic-text-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-basic-text-editor',
    },
})
export class StoryBasicTextEditorComponent extends StoryTextEditorBase {
    readonly froalaOptions = computed(() => ({
        key: this.editorApiKey(),
        ...this.commonFroalaConfig,
        events: {
            ...this.commonFroalaConfig.events,
            initialized: (e: FroalaEditor) => {
                const editorInstance = e.getEditor();
                if (!editorInstance) return;

                this.editor.set(editorInstance);

                if (this.disabled()) {
                    editorInstance.edit.off();
                }

                // Froala's built-in toolbar is always hidden — the custom st-story-toolbar replaces it.
                editorInstance.toolbar.hide();

                editorInstance.events.on(
                    'contentChanged',
                    () => {
                        if (this.isProcessingContentChange) return;
                        // This event was triggered by a programmatic html.set() during story
                        // navigation, not by the user. userHasInteracted may still be true from
                        // typing in the previous story, which would route us into
                        // confirmApprovedStoryEdit below — showing the "confirm edit" modal on
                        // the incoming story without the user having touched it. Even on a
                        // non-approved story the emission would mark it as dirty immediately.
                        // Consume the flag and bail. See programmaticResetPending in the base
                        // class for why the flag is cleared here rather than after html.set().
                        if (this.programmaticResetPending) {
                            this.programmaticResetPending = false;
                            return;
                        }
                        if (!this.userHasInteracted) {
                            this.contentChanged.emit(editorInstance.html.get());
                            return;
                        }
                        this.userHasInteracted = false;
                        this.confirmApprovedStoryEdit(() => this.contentChanged.emit(editorInstance.html.get()));
                    },
                    true
                );

                editorInstance.events.on(EditorActions.SAVE, () => this.save.emit(), true);

                this.applyCommonListeners(editorInstance);
            },
        },
    }));

    protected emitContentChange(html: string): void {
        this.contentChanged.emit(html);
    }

    onProductionCommandClicked(commandType: ProductionCommandType): void {
        this.productionCommandInsertHandler(commandType);
    }
}
